/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @nx/enforce-module-boundaries */
import {
  Component,
  computed,
  ElementRef,
  forwardRef,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { LoadingSpinnerComponent } from './loading-spinner/loading-spinner.component';
import { TextInputComponent } from './text-input/text-input.component';
import { InputStatesService } from './input-states.service';
import { injectVirtualizer } from '@tanstack/angular-virtual';
import { debounce, EMPTY, ReplaySubject, timer } from 'rxjs';
import { RadioButtonComponent } from './radio-button/radio-button.component';
import { CommonModule } from '@angular/common';

export interface Option {
  title: string;
  value: string;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-text-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TextInputComponent,
    LoadingSpinnerComponent,
    RadioButtonComponent,
  ],
  templateUrl: './text-search.component.html',
  styleUrl: './text-search.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TextSearchComponent),
    },
  ],
})
export class TextSearchComponent implements ControlValueAccessor, OnInit {
  scrollElement = viewChild<ElementRef<HTMLDivElement>>('scrollElement');

  readonly placeHolder = input('');
  readonly label = input<string | undefined>();
  readonly name = input.required<string>();
  readonly iconPosition = input<'start' | 'end'>('end');

  options = input<Option[]>([]);
  loading = input(false);
  loadingData = output<boolean>();
  emptyResponse = output();
  showOptions = signal(false);
  guid = input<string>('');
  showSelectedFault = input<boolean>(false);
  selectedLabel = input<string>('Selected fault');
  showSelectedValue = input<boolean>(false);
  selectedValue = input<string | null>(null);
  showRemoveIcon = input<boolean>(false);
  showResultCount = input<boolean>(false);
  textChanged = output<string>();
  exampleText = input<string>('');

  searchTerm = '';
  value: string | undefined;
  touched = false;
  disabled = false;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  searchChanged = new ReplaySubject(1);
  private readonly viewModel = computed(() => {
    return {
      label: this.label(),
      name: this.name(),
      placeHolder: this.placeHolder(),
      loading: this.loading(),
      options: this.options(),
      showOptions: this.showOptions(),
      exampleText: this.exampleText(),
      selectedLabel: this.selectedLabel(),
      showRemoveIcon: this.showRemoveIcon(),
      showSelectedFault: this.showSelectedFault(),
      showSelectedValue: this.showSelectedValue(),
      selectedValue: this.selectedValue(),
    };
  });

  get vm() {
    return this.viewModel();
  }

  virtualiser = injectVirtualizer(() => ({
    scrollElement: this.scrollElement(),
    count: this.options().length,
    estimateSize: () => 71,
    overscan: 5,
  }));

  constructor(private readonly inputStateService: InputStatesService) {}

  onOptionClick(option: Option) {
    this.value = option.value;
    this.onChange(option);
    this.showOptions.set(false);
    this.searchTerm = option.title;
  }

  ngOnInit() {
    this.searchTerm = this.inputStateService.getStoredSearchTerm(this.vm.name);
    this.loadDataFromSearch();
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  loadDataFromSearch(): void {
    // Use a debounce to prevent spam of the endpoints
    this.searchChanged
      .asObservable()
      .pipe(debounce(() => timer(200)))
      .subscribe({
        next: () => {
          this.textChanged.emit(this.searchTerm);
        },
      });
  }

  onSearchChange(event: string) {
    this.searchTerm = event || '';
    this.inputStateService.storeSearchTerm(this.vm.name, this.searchTerm);
    if (event === '') {
      this.showOptions.set(false);
      return;
    }

    this.showOptions.set(true);

    this.loadingData.emit(true);
    this.searchChanged.next(EMPTY);
  }

  handleInputFocus(): void {
    if (this.value) {
      return;
    }

    this.showOptions.set(true);
  }

  removeSelectedValue(): void {
    this.writeValue(undefined);
    this.searchTerm = '';
    this.inputStateService.storeSearchTerm(this.vm.name, '');
    this.showOptions.set(false);
    this.textChanged.emit('');
  }

  onListKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const container = this.scrollElement()?.nativeElement;
    if (!container) return;
    const radios = Array.from(
      container.querySelectorAll<HTMLInputElement>('input[type="radio"]')
    );
    if (!radios.length) return;
    const focusedIndex = radios.indexOf(
      document.activeElement as HTMLInputElement
    );
    if (event.key === 'ArrowDown') {
      radios[Math.min(focusedIndex + 1, radios.length - 1)]?.focus();
    } else {
      radios[Math.max(focusedIndex - 1, 0)]?.focus();
    }
  }
}
